/**
 * The single place that knows how a visit reason carries demand identity.
 *
 * One wire shape, one walk:
 * `reason.haulIdentifier.{ vrHaulIdentifiers: { vrId, locationPlanId }, isaId }`
 *
 * Why this exists rather than each consumer reading the path itself: the walk was
 * duplicated across four sites, and the duplication was not benign. The plan id
 * sits BESIDE the vrId in `vrHaulIdentifiers`, which makes the pairing rule —
 * "a plan id belongs to the haul whose vrId it sits with" — part of the shape
 * rather than part of any one caller. Two copies of that walk drifted: one paired
 * them per reason, the other accumulated each independently and could return one
 * haul's vrId beside another haul's plan id. Since the write path ranks
 * locationPlanId first, that produced goals keyed on the wrong haul. No test
 * could have caught the divergence, because each copy was self-consistent.
 *
 * Returns RAW wire values, never normalized. `normalizeIdentityToken` is applied by the
 * callers that need canonical casing (the derived-VRID sources) and deliberately
 * NOT by the ones that index raw keys (`byVrid` keeps both spellings, because the
 * declared-VRID branch looks up an un-normalized `demandId.VRID`). Normalizing
 * here would silently change what those indexes contain.
 *
 * Absent fields come back as `""` rather than `undefined` so callers can use
 * plain falsy checks and `||=` accumulation without optional-chaining every hop.
 *
 * Pure — no Redux, no React, no I/O.
 */

/**
 * Structural parameter type, deliberately looser than RYC's `VisitReason`.
 *
 * Callers arrive with three different static types for the same runtime object:
 * the typed `VisitReason` (queue transformer), an inline cast off
 * `Record<string, unknown>` (the lookup builder, which receives untyped wire
 * data), and an optional-chained shape (the selectors). A structural type accepts
 * all three without forcing a cast at the call site or dragging the RYC type into
 * this module.
 */
export interface VisitReasonLike {
  haulIdentifier?: {
    vrHaulIdentifiers?: { vrId?: string; locationPlanId?: string } | null;
    isaId?: string | null;
  } | null;
}

/** Demand identity carried by ONE visit reason. `""` means absent. */
export interface VisitReasonIdentity {
  vrId: string;
  isaId: string;
  /**
   * Plan id from the SAME `vrHaulIdentifiers` as `vrId` — the pairing is
   * structural, so a caller cannot accidentally combine them across reasons.
   */
  locationPlanId: string;
}

export const identityFromVisitReason = (
  reason: VisitReasonLike | null | undefined
): VisitReasonIdentity => {
  const haulIdentifier = reason?.haulIdentifier;
  const vrHaulIdentifiers = haulIdentifier?.vrHaulIdentifiers;
  return {
    vrId: vrHaulIdentifiers?.vrId || "",
    isaId: haulIdentifier?.isaId || "",
    locationPlanId: vrHaulIdentifiers?.locationPlanId || "",
  };
};

/**
 * True when a reason identifies a demand at all — it carries a VRID or an ISA.
 *
 * A plan id alone does NOT qualify: it is a planning handle, not an operational
 * identity, and a card or index keyed on one could never match a VRID/ISA-keyed
 * counterpart. Callers filtering "reasons worth reading" want this predicate, not
 * a truthiness check on the whole identity.
 */
export const reasonIdentifiesDemand = (reason: VisitReasonLike | null | undefined): boolean => {
  const { vrId, isaId } = identityFromVisitReason(reason);
  return Boolean(vrId || isaId);
};
